import { AnalyticsUserEvent } from '@fruitful/types';
import { useEffect, useRef } from 'react';
import { isWeb } from 'smoothie';

import { track } from '../../logger/Logger';

const STALE_TIME_MS = 1000 * 60 * 15; // 15 minutes

/**
 * Why do we need this hook?
 *
 * We've noticed that users may keep a single tab open for days. The index.html content in that tab
 * is only loaded when our app is first loaded in that tab. If the user returns to that tab days
 * later, the index.html is not loaded fresh -- the stale content is there, and there's nothing to
 * prompt a request for new content. The problem with stale index.html content is that it will have
 * a reference to a stale javascript bundle -- so the javascript code used will be be as stale as
 * when the index.html was first requested. We'd prefer that users get our latest code as soon as
 * possible so it includes bug fixes and current features.
 */

/**
 * Intended purpose: on web, force a stale page to be refreshed before its used.
 * Intended to be used on the landing/login screen that is shown when a user session has
 * timed out -- the screen that the user will interact with when returning to a stale tab.
 */
export const useRefreshStalePage = () => {
    const pageIdleSince = useRef(Date.now()); // Expected to be when session timed out. The actual time since the original page load (how long ago the index.html and js bundle were requested) could be much longer, depending on how long since the tab was created or last refreshed.

    useEffect(() => {
        const handleFocus = () => {
            const pageIdleTime = Date.now() - pageIdleSince.current;
            if (isWeb && pageIdleTime > STALE_TIME_MS) {
                track(
                    AnalyticsUserEvent.REFRESH_STALE_PAGE,
                    { pageIdleTime },
                    true, // sendImmediately
                );
                window.location.reload();
            }
        };

        isWeb && window.addEventListener('focus', handleFocus);
        return () => {
            isWeb && window.removeEventListener('focus', handleFocus);
        };
    }, []);
};
